import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Percent, Clock, Shield, ExternalLink, MapPin, AlertCircle, X } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country-specific financing data
const canadaFinancingPartners = [
  {
    name: 'Financeit',
    description: 'Canada\'s leading point-of-sale financing provider',
    features: [
      '0% interest options available',
      'Terms from 12 to 120 months',
      'Quick online application',
      'Available across all Canadian provinces'
    ],
    applyUrl: 'https://www.financeit.io/',
    highlight: 'Most Popular in Canada'
  },
  {
    name: 'PayBright by Affirm',
    description: 'Flexible payment plans for Canadian shoppers',
    features: [
      'Pay in 4 interest-free payments',
      'Longer-term financing available',
      'Quick online approval',
      'Accepted at thousands of retailers'
    ],
    applyUrl: 'https://paybright.com/',
    highlight: null
  },
  {
    name: 'Fairstone Financial',
    description: 'Personal loans for major purchases',
    features: [
      'Competitive interest rates',
      'Flexible repayment terms',
      'In-branch or online application',
      'Serving Canadians for over 100 years'
    ],
    applyUrl: 'https://www.fairstone.ca/',
    highlight: null
  }
];

const usaFinancingPartners = [
  {
    name: 'Affirm',
    description: 'America\'s leading buy now, pay later solution',
    features: [
      'Pay in 4 interest-free payments',
      'Monthly payment plans up to 36 months',
      'No hidden fees, ever',
      'Quick online approval process'
    ],
    applyUrl: 'https://www.affirm.com/',
    highlight: 'Most Popular in USA'
  },
  {
    name: 'Bread Financial',
    description: 'Flexible financing for major purchases',
    features: [
      'As low as 0% APR promotional financing',
      'Extended terms up to 48 months',
      'Easy online application',
      'Trusted by major retailers nationwide'
    ],
    applyUrl: 'https://www.breadfinancial.com/',
    highlight: null
  },
  {
    name: 'Synchrony',
    description: 'Home financing solutions',
    features: [
      'Deferred interest plans available',
      'Fixed monthly payment options',
      'Special financing promotions',
      'Nationwide acceptance'
    ],
    applyUrl: 'https://www.synchrony.com/',
    highlight: null
  }
];

const Financing = () => {
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const partnersRef = useRef(null);
  const ctaRef = useRef(null);
  const { currency } = useCurrency();
  
  // Determine country based on currency
  const isCanada = currency === 'CAD';
  const countryName = isCanada ? 'Canada' : 'United States';
  const financingPartners = isCanada ? canadaFinancingPartners : usaFinancingPartners;

  // Check if disclaimer was previously accepted
  useEffect(() => {
    const accepted = sessionStorage.getItem('financing_disclaimer_accepted');
    if (accepted === 'true') {
      setDisclaimerAccepted(true);
      setShowDisclaimerModal(false);
    }
  }, []);

  const handleAcceptDisclaimer = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimerModal(false);
    sessionStorage.setItem('financing_disclaimer_accepted', 'true');
  };

  useEffect(() => {
    const fetchEmbeds = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/financing-embeds`);
        setEmbeds(res.data);
      } catch (e) {
        console.error('Error fetching financing embeds:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchEmbeds();
  }, []);

  const financingOptions = [
    {
      icon: CreditCard,
      title: 'As Low As 0% APR Financing',
      description: isCanada 
        ? 'Qualify for interest-free financing through our Canadian partners on purchases over $1,000 CAD.'
        : 'Qualify for 12-month interest-free financing through our US partners on purchases over $1,000 USD.',
      highlight: 'Most Popular'
    },
    {
      icon: Percent,
      title: 'Low Monthly Payments',
      description: isCanada
        ? 'Spread your purchase into affordable monthly installments starting at $99 CAD/mo.'
        : 'Spread your purchase into affordable monthly installments starting at $99 USD/mo.',
      highlight: null
    },
    {
      icon: Clock,
      title: 'Extended Terms',
      description: isCanada
        ? 'Up to 120 months financing available through Financeit for qualifying Canadian customers.'
        : 'Up to 48 months financing available for qualifying US customers.',
      highlight: null
    },
    {
      icon: Shield,
      title: 'Easy Approval',
      description: 'Quick application process with instant decision. Please check with your financing partners for specific terms and credit requirements.',
      highlight: null
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-36 pb-16 section-padding">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 animate-fade-in">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#dbeef5] text-[#003456] rounded-full text-sm font-medium mb-6">
              <MapPin className="h-4 w-4" />
              Financing Options for {countryName}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4">
              Financing Made Easy
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't let budget hold you back. Our flexible financing options make it easy to bring home your HomeLyph freeze dryer today.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Showing financing options for <strong>{countryName}</strong>. Switch currency in the header to see options for {isCanada ? 'USA' : 'Canada'}.
            </p>
          </div>

          {/* Financing Options */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20 stagger-children">
            {financingOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 card-hover relative">
                {option.highlight && (
                  <span className="absolute -top-3 left-6 px-3 py-1 bg-[#fdc31c] text-white text-xs font-medium rounded-full">
                    {option.highlight}
                  </span>
                )}
                <div className="w-14 h-14 bg-[#dbeef5] rounded-xl flex items-center justify-center mb-4">
                  <option.icon className="h-7 w-7 text-[#003456]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0a0203] mb-2" style={{  }}>
                  {option.title}
                </h3>
                <p className="text-gray-600 text-sm">{option.description}</p>
              </div>
            ))}
          </div>

          {/* Country-Specific Financing Partners */}
          <div className="mb-20" data-testid="financing-partners-section" ref={partnersRef}>
            <h2 className="text-2xl font-bold text-[#0a0203] mb-2 text-center">
              Trusted Financing Partners in {countryName}
            </h2>
            <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
              We've partnered with {isCanada ? 'leading Canadian' : "America's top"} financing providers to offer you flexible payment options.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {financingPartners.map((partner, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 card-hover relative" data-testid={`financing-partner-${index}`}>
                  {partner.highlight && (
                    <span className="absolute -top-3 left-6 px-3 py-1 bg-[#fdc31c] text-[#0a0203] text-xs font-medium rounded-full">
                      {partner.highlight}
                    </span>
                  )}
                  <h3 className="text-xl font-semibold text-[#0a0203] mb-2">{partner.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{partner.description}</p>
                  <ul className="space-y-2 mb-6">
                    {partner.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <span className="text-[#003456] mt-1">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a href={partner.applyUrl} target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[#003456] hover:bg-[#001d30] text-white" data-testid={`apply-${partner.name.toLowerCase().replace(/\s/g, '-')}`}>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Apply with {partner.name}
                    </Button>
                  </a>
                </div>
              ))}
            </div>
            {/* Financing Partners Disclaimer */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid="financing-partners-disclaimer">
              <p className="text-xs text-gray-500">
                <strong>Third-Party Financing Disclosure:</strong> Chinook Manufacturing does not dictate terms or conditions set by third-party financing providers. 
                Financing is subject to credit approval and terms determined solely by the financing partner. 
                By proceeding with financing options, you acknowledge that your information may be shared with our financing partners in accordance with applicable data and privacy policies. 
                All financing offers are subject to change and may vary based on creditworthiness and other factors.
              </p>
            </div>
          </div>

          {/* Calculator Section */}
          <div className="bg-white rounded-3xl p-8 md:p-12 mb-20 animate-fade-in">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#0a0203] mb-4">
                  Estimate Your Monthly Payment
                </h2>
                <p className="text-gray-600 mb-6">
                  {isCanada 
                    ? 'Use one of our Canadian financing partner calculators to see what your monthly payments could look like. Get pre-approved in minutes without affecting your credit score.'
                    : 'Use one of our US financing partner calculators to see what your monthly payments could look like. Get pre-approved in minutes without affecting your credit score.'
                  }
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#dbeef5] rounded-full flex items-center justify-center text-[#003456] font-bold text-sm">1</div>
                    <span className="text-[#0a0203]">Select your product</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#dbeef5] rounded-full flex items-center justify-center text-[#003456] font-bold text-sm">2</div>
                    <span className="text-[#0a0203]">Choose your term length</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#dbeef5] rounded-full flex items-center justify-center text-[#003456] font-bold text-sm">3</div>
                    <span className="text-[#0a0203]">Get instant pre-approval</span>
                  </div>
                </div>
              </div>
              <div className="bg-[#f0f4f8] rounded-2xl p-8">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">Example Monthly Payment</p>
                  <p className="text-5xl font-bold text-[#003456] mb-2" style={{ fontFamily: 'JetBrains Mono' }}>
                    ${isCanada ? '139' : '104'}<span className="text-2xl">/mo</span>
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    HomeLyph Pro 500 • 24 months • as low as 0% APR • {currency}
                  </p>
                  <Link to="/products">
                    <Button className="bg-[#003456] hover:bg-[#001d30] text-white btn-scale w-full" data-testid="apply-financing-btn">
                      Shop Products
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Admin-Embedded Financing Partners (if any) */}
          {embeds.length > 0 && (
            <div className="mb-20">
              <h2 className="text-2xl font-bold text-[#0a0203] mb-8 text-center">
                Additional Financing Tools
              </h2>
              <div className="space-y-8">
                {embeds.map((embed) => (
                  <div key={embed.id} className="bg-white rounded-2xl p-6 overflow-hidden" data-testid={`financing-embed-${embed.id}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-[#0a0203]">{embed.name}</h3>
                        {embed.description && (
                          <p className="text-gray-500 text-sm">{embed.description}</p>
                        )}
                      </div>
                      <a 
                        href={embed.embed_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#003456] hover:underline text-sm"
                      >
                        Open in new tab <ExternalLink className="h-4 w-4" />
                      </a>
                    </div>
                    <div className="aspect-video rounded-xl overflow-hidden border border-gray-200">
                      <iframe
                        src={embed.embed_url}
                        className="w-full h-full"
                        title={embed.name}
                        loading="lazy"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Section */}
          <div className="animate-fade-in">
            <h2 className="text-2xl font-bold text-[#0a0203] mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  q: 'Will applying affect my credit score?',
                  a: 'Credit check policies vary by financing provider. Please check with your financing partner directly for details on their credit inquiry process.'
                },
                {
                  q: 'What are the minimum requirements?',
                  a: 'Requirements vary by financing partner. Please check with your financing partner for their specific eligibility requirements and qualification criteria.'
                },
                {
                  q: 'Can I pay off my loan early?',
                  a: 'Early payment options and any associated fees vary by financing partner. Please check with your financing partner for their specific prepayment policies.'
                },
                {
                  q: 'How long does approval take?',
                  a: 'Approval times vary by financing partner. Please check with your financing partner for their specific application and approval timeline.'
                },
                {
                  q: isCanada ? 'Is financing available in all provinces?' : 'Is financing available in all states?',
                  a: 'Service areas and terms vary by financing partner. Please check with your financing partner for availability in your specific location.'
                },
                {
                  q: 'What interest rates are available?',
                  a: 'Interest rates, APR, and promotional offers vary by financing partner and are subject to credit approval. Please check with your financing partner for current rates and terms.'
                }
              ].map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl p-6" data-testid={`faq-${i}`}>
                  <h4 className="font-semibold text-[#0a0203] mb-2">{faq.q}</h4>
                  <p className="text-gray-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-20 bg-[#003456]" ref={ctaRef}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started in {countryName}?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Don't wait to start preserving your food. Apply for financing today with our trusted {isCanada ? 'Canadian' : 'American'} partners and get your HomeLyph freeze dryer.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button className="bg-white text-[#003456] hover:bg-gray-100 px-8 py-6 btn-scale" data-testid="shop-with-financing-btn">
                Shop with Financing
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 btn-scale">
                Talk to an Expert
              </Button>
            </Link>
          </div>
          {/* CTA Section Financing Disclaimer */}
          <p className="text-xs text-white/60 mt-8 max-w-2xl mx-auto" data-testid="financing-cta-disclaimer">
            <strong>Third-Party Financing Disclosure:</strong> Chinook Manufacturing does not dictate terms or conditions set by third-party financing providers. 
            Financing is subject to credit approval and terms determined solely by the financing partner. 
            By proceeding with financing options, you acknowledge that your information may be shared with our financing partners in accordance with applicable data and privacy policies.
          </p>
        </div>
      </section>

      {/* Front and Center Disclaimer Modal */}
      {showDisclaimerModal && !disclaimerAccepted && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-fade-in p-4" data-testid="financing-disclaimer-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8 md:p-10 relative">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-12 w-12 text-amber-600" />
              </div>
            </div>
            
            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold text-[#0a0203] text-center mb-4">
              Third-Party Financing Disclosure
            </h2>
            
            {/* Disclaimer Content */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6 max-h-64 overflow-y-auto">
              <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                <strong>Important:</strong> Chinook Manufacturing does not dictate terms or conditions set by third-party financing providers. 
                Financing is subject to credit approval and terms determined solely by the financing partner.
              </p>
              <p className="text-gray-700 leading-relaxed text-sm md:text-base mt-4">
                By proceeding with financing options, you acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-700 text-sm md:text-base mt-2 space-y-2">
                <li>Your information may be shared with our financing partners</li>
                <li>All financing offers are subject to change</li>
                <li>Terms may vary based on creditworthiness and other factors</li>
                <li>You should review each financing partner's specific terms and privacy policies</li>
              </ul>
            </div>
            
            {/* Accept Button */}
            <Button 
              onClick={handleAcceptDisclaimer}
              className="w-full bg-[#003456] hover:bg-[#001d30] text-white py-6 text-lg"
              data-testid="accept-financing-disclaimer"
            >
              I Understand, Continue to Financing Options
            </Button>
            
            {/* Small Print */}
            <p className="text-xs text-gray-400 text-center mt-4">
              By clicking continue, you acknowledge you have read and understood this disclosure.
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Financing;
